<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-title>Foto evento</ion-title>
        <ion-buttons slot="start">
          <ion-button @click="closeModal()">
            <ion-icon slot="start" :icon="arrowBackOutline" color="white"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content :fullscreen="true">
      <div class="page_content">
        <div v-if="gallery.length === 0" class="empty_gallery">Nessuna foto in galleria</div>
        <div v-else class="gallery_grid">
          <div v-for="(img, idx) in gallery" :key="idx" class="gallery_img_wrapper">
            <img :src="getImageUrl(img)" :alt="img.original_filename || 'gallery'" @click="openImgModal(idx)" />
          </div>
        </div>
      </div>
      <!-- Modale immagine ingrandita -->
      <Transition name="fade-transition">
        <div v-show="showImgModal" class="attachment_modal" @click="closeImgModal">
          <!-- Contenitore principale con swipe support -->
          <div 
            class="modal_content"
            @click.stop
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
          >
            <!-- Header con contatore e pulsanti -->
            <div class="modal_header">
              <div class="image_counter">{{ currentImageIndex + 1 }} / {{ gallery.length }}</div>
              <!-- <div class="action_buttons">
                <ion-button fill="clear" @click="downloadImage" class="action_btn">
                  <ion-icon :icon="downloadOutline" color="white"></ion-icon>
                </ion-button>
                <ion-button fill="clear" @click="shareImage" class="action_btn">
                  <ion-icon :icon="shareOutline" color="white"></ion-icon>
                </ion-button>
              </div> -->
            </div>

            <!-- Container immagine -->
            <div class="image_container">
              <!-- Pulsante precedente -->
              <div 
                v-if="gallery.length > 1"
                class="nav_button nav_prev"
                @click="previousImage"
              >
                <ion-icon :icon="chevronBackOutline" color="white"></ion-icon>
              </div>

              <!-- Immagine -->
              <div class="image_wrapper">
                <img :src="getCurrentImageUrl()" alt="Immagine evento" />
              </div>

              <!-- Pulsante successivo -->
              <div 
                v-if="gallery.length > 1"
                class="nav_button nav_next"
                @click="nextImage"
              >
                <ion-icon :icon="chevronForwardOutline" color="white"></ion-icon>
              </div>
            </div>

            <!-- Indicatori -->
            <div v-if="gallery.length > 1" class="indicators">
              <div 
                v-for="(img, idx) in gallery" 
                :key="idx"
                class="indicator"
                :class="{ active: idx === currentImageIndex }"
                @click="goToImage(idx)"
              ></div>
            </div>
          </div>

          <!-- Pulsante chiudi -->
          <div @click="closeImgModal()" class="close_modal">Chiudi</div>
        </div>
      </Transition>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, modalController } from "@ionic/vue";
import { ref, computed } from "vue";
import { arrowBackOutline, downloadOutline, shareOutline, chevronBackOutline, chevronForwardOutline } from "ionicons/icons";

export default {
  name: "EventGalleryDetail",
  components: {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    IonButton,
    IonIcon,
  },
  props: {
    data: {
        type: Object,
        required: true,
    },
  },
  setup(props, context) {
    const event = ref({ ...props.data });
    const gallery = computed(() => {
      if (!event.value.events_gallery) return [];
      try {
        return JSON.parse(event.value.events_gallery);
      } catch {
        return [];
      }
    });

    function getImageUrl(img) {
      if (img && img.path_local) {
        return `${process.env.VUE_APP_BASE_URL}/uploads/${img.path_local}`;
      }
      return '';
    }

    // Modal logic
    const showImgModal = ref(false);
    const currentImageIndex = ref(0);

    function openImgModal(index) {
      showImgModal.value = true;
      currentImageIndex.value = index;
    }

    function closeImgModal() {
      showImgModal.value = false;
    }

    function getCurrentImageUrl() {
      if (gallery.value && gallery.value[currentImageIndex.value]) {
        return getImageUrl(gallery.value[currentImageIndex.value]);
      }
      return '';
    }

    // Navigation functions
    function nextImage() {
      if (currentImageIndex.value < gallery.value.length - 1) {
        currentImageIndex.value++;
      } else {
        currentImageIndex.value = 0; // Loop to first
      }
    }

    function previousImage() {
      if (currentImageIndex.value > 0) {
        currentImageIndex.value--;
      } else {
        currentImageIndex.value = gallery.value.length - 1; // Loop to last
      }
    }

    function goToImage(index) {
      currentImageIndex.value = index;
    }

    // Touch/Swipe handling
    const touchStartX = ref(0);
    const touchStartY = ref(0);
    const minSwipeDistance = 50;

    function handleTouchStart(event) {
      touchStartX.value = event.touches[0].clientX;
      touchStartY.value = event.touches[0].clientY;
    }

    function handleTouchMove(event) {
      // Prevent default scroll behavior during swipe
      event.preventDefault();
    }

    function handleTouchEnd(event) {
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.value;
      const deltaY = touchEndY - touchStartY.value;
      
      // Check if it's a horizontal swipe (more horizontal than vertical)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          // Swipe right - previous image
          previousImage();
        } else {
          // Swipe left - next image
          nextImage();
        }
      }
    }

    // Download function
    /* async function downloadImage() {
      const currentImage = gallery.value[currentImageIndex.value];
      if (!currentImage) return;

      try {
        const imageUrl = getCurrentImageUrl();
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = currentImage.original_filename || `evento_foto_${currentImageIndex.value + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Errore durante il download:', error);
      }
    } */

    // Share function
    /* async function shareImage() {
      const currentImage = gallery.value[currentImageIndex.value];
      if (!currentImage) return;

      try {
        const imageUrl = getCurrentImageUrl();
        
        // Check if Web Share API is available
        if (navigator.share) {
          // For sharing images, we need to convert to blob first
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const file = new File([blob], currentImage.original_filename || 'evento_foto.jpg', { type: blob.type });
          
          await navigator.share({
            title: 'Foto evento',
            text: 'Guarda questa foto dell\'evento',
            files: [file]
          });
        } else {
          // Fallback: copy link to clipboard
          await navigator.clipboard.writeText(imageUrl);
          // You might want to show a toast message here
          alert('Link copiato negli appunti');
        }
      } catch (error) {
        console.error('Errore durante la condivisione:', error);
        // Fallback: copy link to clipboard
        try {
          const imageUrl = getCurrentImageUrl();
          await navigator.clipboard.writeText(imageUrl);
          alert('Link copiato negli appunti');
        } catch (clipboardError) {
          console.error('Errore durante la copia negli appunti:', clipboardError);
        }
      }
    } */

    /**
     * ! Handle close modal by clicking on Chiudi button
     */
    const closeModal = () => {
        modalController.dismiss();
    };

    return {
      event,
      gallery,
      getImageUrl,
      showImgModal,
      currentImageIndex,
      openImgModal,
      closeImgModal,
      getCurrentImageUrl,
      nextImage,
      previousImage,
      goToImage,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      // downloadImage,
      // shareImage,
      closeModal,
      arrowBackOutline,
      downloadOutline,
      shareOutline,
      chevronBackOutline,
      chevronForwardOutline,
    };
  },
};
</script>

<style scoped>
ion-toolbar {
    --background: #086fa3;
    --color: #ffffff;
}
ion-button {
    --color: #ffffff;
}

.loading_spinner {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}
.page_content {
  width: 100%;
  padding: 16px;
  min-height: 100%;
  background: #f2f2f2;
}
.empty_gallery {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background-color: rgb(226 232 240);
  color: rgb(148 163 184);
  font-size: 16px;
  font-weight: 500;
  border-radius: 4px;
}
.gallery_grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.gallery_img_wrapper {
  width: 100%;
  aspect-ratio: 1/1;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px 0 #0001;
  cursor: pointer;
}
.gallery_img_wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

/* Modale immagine ingrandita */
.attachment_modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal_content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.modal_header {
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  z-index: 1000;
  pointer-events: none;
}

.image_counter {
  color: white;
  font-size: 16px;
  font-weight: 600;
  background: rgba(0,0,0,0.8);
  padding: 8px 16px;
  border-radius: 20px;
  border: 2px solid rgba(255,255,255,0.3);
  backdrop-filter: blur(10px);
  pointer-events: auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.action_buttons {
  display: flex;
  gap: 8px;
  pointer-events: auto;
}

.action_btn {
  --background: rgba(0,0,0,0.8);
  --border-radius: 50%;
  --color: white;
  width: 48px;
  height: 48px;
  margin: 0;
  border: 2px solid rgba(255,255,255,0.3);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.image_container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 120px 20px 100px;
}

.image_wrapper {
  max-width: 90vw;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image_wrapper img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.2);
}

.nav_button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: rgba(0,0,0,0.8);
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 1000;
  backdrop-filter: blur(10px);
  color: white;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.nav_button:hover {
  background: rgba(0,0,0,0.9);
  border-color: rgba(255,255,255,0.5);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(0,0,0,0.4);
}

.nav_prev {
  left: 20px;
}

.nav_next {
  right: 20px;
}

.indicators {
  position: absolute;
  bottom: 180px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 1000;
  padding: 8px 16px;
  background: rgba(0,0,0,0.8);
  border-radius: 20px;
  border: 2px solid rgba(117, 23, 23, 0.3);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255,255,255,0.4);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(255,255,255,0.2);
}

.indicator.active {
  background: white;
  transform: scale(1.2);
  box-shadow: 0 0 8px rgba(255,255,255,0.5);
}

.close_modal {
  position: absolute;
  bottom: 160px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  background: #086fa3;
  padding: 14px 36px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
  transition: all 0.2s;
  z-index: 1000;
  border: 2px solid rgba(255,255,255,0.3);
  backdrop-filter: blur(10px);
  font-size: 16px;
}

.close_modal:hover {
  background: #05507a;
  transform: translateX(-50%) scale(1.05);
  box-shadow: 0 6px 20px rgba(0,0,0,0.4);
}

/* Fade transition */
.fade-transition-enter-active, .fade-transition-leave-active {
  transition: opacity 0.2s;
}
.fade-transition-enter-from, .fade-transition-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal_header {
    top: 50px;
    padding: 0 15px;
  }
  
  .image_container {
    padding: 100px 15px 160px;
  }
  
  .nav_button {
    width: 44px;
    height: 44px;
  }
  
  .nav_prev {
    left: 15px;
  }
  
  .nav_next {
    right: 15px;
  }
  
  .indicators {
    bottom: 160px;
    padding: 6px 12px;
  }
  
  .indicator {
    width: 8px;
    height: 8px;
  }
  
  .close_modal {
    bottom: 100px;
    padding: 12px 28px;
    font-size: 15px;
  }
  
  .action_btn {
    width: 42px;
    height: 42px;
  }
  
  .image_counter {
    font-size: 14px;
    padding: 6px 12px;
  }
}
</style> 